<script setup>
import {computed, provide, reactive, readonly, ref, watch} from "vue";
import AutoComplete from "primevue/autocomplete";
import FloatLabel from "primevue/floatlabel";
import {gregorianDate} from "@/utils/gregorianDate.js";
import {useUserStore} from "@/stores/user.js";
import {required} from "@vuelidate/validators";
import {useVuelidate} from "@vuelidate/core";
import UserProfile from "@/components/UserProfile/index.vue";

const userStore = useUserStore();
const loading = ref(false)
const isShowUserProfileModal = ref(false);
const canShowUserProfile = computed(() => userStore.hasPermission('BIG_BOSS') || userStore.hasPermission('ADMIN'))
// Props
const props = defineProps({
  modelValue: Object,
  appendTo: {default: "body"},
  label: {default: "User"},
  containerClass: {type: String},
  accountName: {type: String},
  accountID: {type: [String, Number]},
  required: {type: Boolean, default: true},
  disabled: {type: Boolean, default: false},
  readonly: {type: Boolean, default: false}
});

const form = reactive({
  "userID": "",
})
const rules = computed(() => {
  return {
    userID: props.required ? {required} : {}
  };
});
const $vulidate = useVuelidate(rules, form);

const selectedUser = ref({})
const serverUsers = ref([])

// Emits
const emit = defineEmits(["update:modelValue", "select"]);

async function onFilter(event) {
  const keyword = event.query?.trim()
  if (!keyword || keyword.length < 3) {
    serverUsers.value = []
    loading.value = false
    return
  }

  loading.value = true
  try {
    const result = await userStore.action_searchAccounts({keyword})
    serverUsers.value = result || []
  } finally {
    loading.value = false
  }
}

async function changeTaker() {
  if (!selectedUser.value?.id) {
    form.userID = null
    emit('update:modelValue', null)
    emit('select', null)
    return
  }

  form.userID = selectedUser.value.id
  emit('select', selectedUser.value)
  emit('update:modelValue', selectedUser.value)
}

watch(selectedUser, (newVal) => {
  if (!props.required && (!newVal || Object.keys(newVal).length === 0 || !newVal.id)) {
    form.userID = null
    emit('update:modelValue', null)
    emit('select', null)
  }
}, {deep: true})

function getDiscordCreationDate(entityId) {
  if (!entityId) return null;
  const TIMESTAMP_OFFSET = 22n;
  const DISCORD_EPOCH = 1420070400000n;
  const id = BigInt(entityId);
  const timestamp = (id >> TIMESTAMP_OFFSET) + DISCORD_EPOCH;
  return gregorianDate(Number(timestamp), 'HH:mm - DD MMM YYYY');
}

const optionLabel = (item) => {
  if (!item) return '';
  if (item.name && item.discordUsername) {
    return `${item.name} - ${item.discordUsername}`;
  }
  return item.name ?? '';
};

watch(() => props.modelValue, (newVal) => {
  if (newVal && typeof newVal === 'object') {
    selectedUser.value = newVal
    form.userID = newVal.id
  }
}, {immediate: true})

function showUserProfileModal(user) {
  if (user.isLoading) return;
  if (canShowUserProfile.value) {
    userStore.setSelectedUser(user);
    isShowUserProfileModal.value = true
  }
}

provide('isShowUserProfileModal', isShowUserProfileModal)
</script>
<template>
  <div class="flex-1 flex flex-col gap-0.5" :class="containerClass">
    <FloatLabel class="w-full custom-select" :class="{ 'has-error': $vulidate.userID.$error}" variant="on">
      <AutoComplete v-model="selectedUser" input-id="userID" :spellcheck="false"
                    :suggestions="serverUsers" :forceSelection="!!props.required" :showClear="!props.required"
                    @complete="onFilter"
                    completeOnFocus
                    :optionLabel="optionLabel" @itemSelect="changeTaker" :minLength="3" :delay="1000"
                    :loading="loading" class="w-full !rounded-md"
                    :class="[$vulidate.userID.$error ? '!border !border-red-300' : '']"
                    input-class="w-full min-w-28 !bg-transparent h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                    overlay-class="!bg-[#0F172A] !rounded-md" :append-to="appendTo"
                    :virtualScrollerOptions="{ itemSize: 48 }"
                    dropdown :disabled="disabled" :readonly="readonly">
        <template #option="slotProps">
          <div class="flex items-center gap-2 text-sm">
            <div @click.stop="showUserProfileModal(slotProps.option)"
                 :class="[canShowUserProfile ? 'cursor-zoom-in' : 'cursor-default']">
              <img v-if="slotProps.option.discordAvatar" :alt="slotProps.option.name"
                   :src="slotProps.option.discordAvatar"
                   class="size-10 rounded-full border border-slate-700"/>
              <div v-else class="flex items-center justify-center size-10 rounded-full border border-slate-700">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                     class="lucide lucide-user-icon lucide-user">
                  <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/>
                  <circle cx="12" cy="7" r="4"/>
                </svg>
              </div>
            </div>
            <div class="flex flex-col">
              <div class="flex flex-row gap-2 items-center">
                <div>{{ slotProps.option.name }} &nbsp &nbsp</div>
                <div v-if="slotProps.option.rating" class="flex flex-row items-center gap-0.5 text-xs">
                  <span class="flex flex-row items-end">
                    {{ slotProps.option.rating }}⭐ ({{ slotProps.option.ratingCount }})
                  </span>
                </div>
              </div>
              <div class="flex flex-row w-max text-xs text-slate-400">
                <div>{{ slotProps.option.discordUsername ?? "-" }}</div>
                <div>{{ "&nbsp- " + slotProps.option.discordDisplayName ?? "-" }}</div>
              </div>
            </div>
          </div>
        </template>
      </AutoComplete>
      <label for="userID" class="!bg-slate-900">{{ label }}</label>
    </FloatLabel>
    <template v-if="$vulidate.userID.$error">
      <p class="text-red-300 mt-0.5 2xl:mt-1 text-[.6rem] 2xl:text-xs"
         v-if="!$vulidate.userID.required.$response">
        This field is required
      </p>
    </template>
  </div>
  <UserProfile v-if="isShowUserProfileModal" :userID="userStore.selectedUser.id"/>
</template>